import { getAssistance } from "../../lib/admin-assistance";
import { AppUpdateBanner } from "../../components/AppUpdateBanner";
import { postProviderWorkingLocation, PROVIDER_WORKING_LOCATION_EVENT, signalProviderWorkspaceSignOut, type ProviderWorkingLocation } from "./provider-working-location";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Bell, Languages, MoonStar, SunMedium, Volume2, VolumeX, X } from "lucide-react";
import { setSoundEnabled, useActivityAlerts, useSoundEnabled } from "../../lib/activity-alerts";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { Button } from "../../components/ui/Button";
import { useHardConfirm } from "../../components/HardConfirmDialog";
import { useAuth } from "../../lib/auth";
import { ApiError, providerApi } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import {
  startRealtimeEvents,
  stopRealtimeEvents,
} from "../../lib/realtime";
import { useProviderOffline } from "../../lib/provider-offline";
import type { QueuedProviderMutation } from "../../lib/offline-store";
import { getVenueStatus } from "../provider-availability/availability";
import { usePendingConfirmations } from "../provider-bookings/usePendingConfirmations";
import { useProviderSessions } from "../provider-sessions/useProviderSessions";
import { useFoodOrderQueue } from "../provider-orders/useFoodOrderQueue";
import { NavBadgeChip, type NavBadge } from "./NavBadge";
import { ProviderApprovalBlockedPage } from "./ProviderApprovalBlockedPage";
import { ProviderNotificationsPanel } from "./ProviderNotificationsPanel";
import {
  useProviderNotifications,
  type ProviderNotificationItem,
} from "./useProviderNotifications";
import { useProviderWorkspace } from "./useProviderWorkspace";
import { ProviderWorkspaceProvider } from "./ProviderWorkspaceContext";
import {
  ProviderWorkspaceFormProvider,
  type ProviderWorkspaceFormValue,
} from "./ProviderWorkspaceFormContext";
import { MobileHeader } from "./MobileHeader";
import {
  countText,
  formatLabel,
  isProviderSection,
  membershipRoleLabel,
  primarySections,
  roleLabel,
  secondarySections,
  sectionIcons,
  sectionLabelKeys,
  VenueSwitcher,
  venueCanOperate,
  type ProviderSection,
} from "./provider-workspace-helpers";
import { useProviderElevation } from "./ProviderElevationDialog";
import {
  parseProviderNotificationTarget,
  resolveAndRouteProviderNotificationTarget,
  type ProviderNotificationAuthorityTarget,
  type ProviderNotificationResolvedTarget,
} from "./provider-notification-target";

const PROVIDER_BASE_PATH = "/provider" as const;

function getProviderSectionPath(section: ProviderSection) {
  return `${PROVIDER_BASE_PATH}/${section}`;
}

export function DeadLetterPanel({
  deadLetters,
  onRetry,
  onDismiss,
}: {
  deadLetters: QueuedProviderMutation[];
  onRetry: (mutationId: string) => void;
  onDismiss: (mutationId: string) => void;
}) {
  if (deadLetters.length === 0) return null;
  return (
    <div className="ops-surface ops-surface-dense" data-testid="dead-letter-panel">
      <p className="text-sm font-semibold text-white">Queued changes needing attention</p>
      <div className="mt-2 space-y-2">
        {deadLetters.map((mutation) => (
          <div className="flex flex-wrap items-center justify-between gap-2" data-testid="dead-letter" key={mutation.id}>
            <div className="min-w-0">
              <p className="text-sm text-white">{mutation.label}</p>
              <p className="text-xs text-amber-100">{mutation.dead_letter_reason ?? "Terminal sync error"}</p>
            </div>
            <div className="flex gap-2">
              <button className="ops-button" data-variant="secondary" onClick={() => onRetry(mutation.id)} type="button">Retry</button>
              <button className="ops-button" data-variant="secondary" onClick={() => onDismiss(mutation.id)} type="button">Dismiss</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProviderLayout() {
  const { signOut, user } = useAuth();
  const handleProviderSignOut = useCallback(async () => {
    signalProviderWorkspaceSignOut();
    await signOut();
  }, [signOut]);
  const {
    language,
    setLanguage,
    setThemePreference,
    t,
    themePreference,
  } = useAppPreferences();
  const { confirm: confirmDestructiveAction, dialog: destructiveActionDialog } = useHardConfirm();
  const { dialog: elevationDialog } = useProviderElevation();
  const {
    isOnline,
    isSyncing,
    lastSyncError,
    pendingCount,
    deadLetters,
    retryDeadLetter,
    dismissDeadLetter,
    syncPendingMutations,
  } = useProviderOffline();
  const navigate = useNavigate();
  const location = useLocation();
  const notificationTarget = useMemo(
    () => parseProviderNotificationTarget(location.search),
    [location.search],
  );
  const mainScrollRef = useRef<HTMLElement | null>(null);
  const lastNotificationTargetKeyRef = useRef<string | null>(null);
  const {
    summary,
    provider,
    venues,
    selectedVenue,
    selectedUnit,
    selectedVenueId,
    selectedUnitId,
    setSelectedVenueId,
    setSelectedUnitId,
    isLoading,
    loadError,
    saveError,
    notice,
    creatingVenue,
    updatingVenue,
    deletingVenue,
    creatingUnit,
    updatingUnit,
    deletingUnit,
    updatingAvailability,
    creatingBlackoutWindow,
    deletingBlackoutWindowId,
    createVenue,
    updateVenue,
    deleteVenue,
    createUnit,
    updateUnit,
    deleteUnit,
    updateAvailability,
    createBlackoutWindow,
    deleteBlackoutWindow,
    applyOfflineSessionStart,
    applyOfflineSessionUpdate,
    applyOfflineSessionStop,
    reload,
  } = useProviderWorkspace();
  const notificationIdentityKey = summary.provider_status === "approved" && user?.id && provider?.id
    ? `${user.id}:${provider?.id ?? ""}:${user.role ?? ""}`
    : null;
  const notificationResolutionGenerationRef = useRef(0);
  const notificationIdentityKeyRef = useRef<string | null>(notificationIdentityKey);
  notificationIdentityKeyRef.current = notificationIdentityKey;
  const isCurrentNotificationResolution = useCallback((identityKey: string | null, generation: number) => (
    identityKey !== null &&
    identityKey === notificationIdentityKeyRef.current &&
    generation === notificationResolutionGenerationRef.current
  ), []);
  const [operationsVersion, setOperationsVersion] = useState(0);
  const [notificationError, setNotificationError] = useState<{ message: string; requiresSignIn: boolean } | null>(null);
  const [createBookingRequestToken, setCreateBookingRequestToken] = useState(0);
  const [pendingReviewToken, setPendingReviewToken] = useState(0);
  const [dismissedPendingIds, setDismissedPendingIds] = useState<string[]>([]);
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null);
  const [venuePanel, setVenuePanel] = useState<import("./ProviderOutletContext").VenuePanel | null>(null);
  const [venueRemovalNote, setVenueRemovalNote] = useState("");
  const [venueRemovalError, setVenueRemovalError] = useState<string | null>(null);
  const previousSelectedVenueIdRef = useRef(selectedVenueId);

  useLayoutEffect(() => {
    if (previousSelectedVenueIdRef.current === selectedVenueId) return;
    previousSelectedVenueIdRef.current = selectedVenueId;
    setVenuePanel(null);
    setVenueRemovalNote("");
    setVenueRemovalError(null);
  }, [selectedVenueId]);

  const formControllerRef = useRef<ProviderWorkspaceFormValue>({
    operationsVersion,
    setOperationsVersion,
    createBookingRequestToken,
    setCreateBookingRequestToken,
    pendingReviewToken,
    setPendingReviewToken,
    checkoutSessionId,
    setCheckoutSessionId,
    venuePanel,
    setVenuePanel,
    venueRemovalNote,
    setVenueRemovalNote,
    venueRemovalError,
    setVenueRemovalError,
  });

  formControllerRef.current = {
    operationsVersion,
    setOperationsVersion,
    createBookingRequestToken,
    setCreateBookingRequestToken,
    pendingReviewToken,
    setPendingReviewToken,
    checkoutSessionId,
    setCheckoutSessionId,
    venuePanel,
    setVenuePanel,
    venueRemovalNote,
    setVenueRemovalNote,
    venueRemovalError,
    setVenueRemovalError,
  };

  const totalUnits = venues.reduce((count, venue) => count + venue.units.length, 0);
  const occupiedUnits = venues.reduce(
    (count, venue) =>
      count + venue.units.filter((unit) => unit.current_session !== null).length,
    0,
  );
  const blockedUnits = venues.reduce(
    (count, venue) =>
      count + venue.units.filter((unit) => unit.status === "blocked").length,
    0,
  );

  const syncOperationalState = useCallback(
    async (preferredVenueId?: string | null, preferredUnitId?: string | null) => {
      await reload(preferredVenueId, preferredUnitId);
      setOperationsVersion((current) => current + 1);
    },
    [reload],
  );

  const { pendingCount: pendingConfirmationCount, pendingIds: pendingConfirmationIds } =
    usePendingConfirmations(summary.provider_status === "approved", selectedVenueId);
  const { openCount: openOrderCount, newCount: newOrderCount } = useFoodOrderQueue(
    summary.provider_status === "approved" && !getAssistance() ? selectedVenueId : null,
    { includeRecent: false },
  );
  // Desktop notifications open in a drawer over the page; it always starts closed.
  const [sidebarNotificationsOpen, setSidebarNotificationsOpen] = useState(false);
  const soundEnabled = useSoundEnabled();
  useActivityAlerts(summary.provider_status === "approved" && !getAssistance(), {
    order: t("New food order"),
    booking: t("New booking request"),
    extension: t("extensions.notification"),
  });
  useEffect(() => {
    if (!sidebarNotificationsOpen) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setSidebarNotificationsOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sidebarNotificationsOpen]);
  const {
    markAllNotificationsRead,
    readBusy: notificationReadBusy,
    readError: notificationReadError,
    markNotificationRead,
    markNotificationTargetUnavailable,
    notifications,
    inboxState: notificationInboxState,
    loadMore: loadMoreNotifications,
    pagination: notificationPagination,
    preferences: notificationPreferences,
    reload: reloadNotifications,
    reloadPreferences: reloadNotificationPreferences,
    unreadCount: unreadFeedCount,
    updatePreference: updateNotificationPreference,
  } = useProviderNotifications(summary.provider_status === "approved" && !getAssistance(), null, {
    providerId: provider?.id ?? null,
    role: user?.role ?? null,
    userId: user?.id ?? null,
  });
  // Pending bookings are actionable work; the durable inbox owns unread state.
  // Reading a notification must not re-light its badge after a page reload.
  const pendingUnread = pendingConfirmationIds.some(bookingId =>
    !dismissedPendingIds.includes(bookingId) && notifications.some(item =>
      !item.read && "dto" in item && item.dto.target.bookingId === bookingId));
  const unreadNotificationsCount = unreadFeedCount;

  const {
    sessions,
    recentSessions,
    activeSessionsByBookingId,
    creatingWalkIn,
    error: sessionError,
    isLoading: sessionsLoading,
    notice: sessionNotice,
    startSessionFromBooking,
    startWalkInSession,
    startingBookingId,
    extendSession,
    extendingSessionId,
    stopSession,
    stoppingSessionId,
    reload: reloadSessions,
  } = useProviderSessions({
    notificationSessionId: notificationTarget && "sessionId" in notificationTarget ? notificationTarget.sessionId : null,
    applyOfflineSessionStart,
    applyOfflineSessionUpdate,
    applyOfflineSessionStop,
    selectedVenue,
    syncOperationalState,
  });

  const handleReceiptChange = useCallback(async () => {
    await reloadSessions();
    await syncOperationalState(selectedVenue?.id ?? null, selectedUnit?.id ?? null);
  }, [reloadSessions, selectedUnit?.id, selectedVenue?.id, syncOperationalState]);

  const selectedVenueOpenState = selectedVenue
    ? getVenueStatus(selectedVenue, t)
    : null;
  const selectedVenueLiveUnits =
    selectedVenue?.units.filter((unit) => unit.current_session !== null).length ?? 0;
  const selectedVenueBlockedUnits =
    selectedVenue?.units.filter((unit) => unit.status === "blocked").length ?? 0;
  const selectedVenueFreeUnits =
    selectedVenue?.units.filter(
      (unit) =>
        unit.current_session === null &&
        unit.status !== "blocked" &&
        unit.status !== "offline",
    ).length ?? 0;
  const selectedVenueCanOperate = selectedVenue ? venueCanOperate(selectedVenue.approval_status) : false;

  const handleSelectVenue = useCallback(
    (venueId: string) => {
      const nextVenue = venues.find((venue) => venue.id === venueId) ?? null;
      setSelectedVenueId(venueId);
      setSelectedUnitId(nextVenue?.units[0]?.id ?? null);
      formControllerRef.current.setVenuePanel(null);
    },
    [venues, setSelectedVenueId, setSelectedUnitId],
  );

  const activeSection: ProviderSection = useMemo(() => {
    const candidate = location.pathname.startsWith(`${PROVIDER_BASE_PATH}/`)
      ? location.pathname.slice(PROVIDER_BASE_PATH.length + 1).split("/")[0] ?? null
      : null;
    return isProviderSection(candidate) ? candidate : "dashboard";
  }, [location.pathname]);

  // Child views report only the record visibly selected; the native host validates
  // this account and semantic payload again before saving a bookmark.
  useLayoutEffect(() => {
    // The native host only knows the original sections; newer ones are not bookmarked.
    if (!user || !selectedVenue || notificationError || activeSection === "tournaments" || activeSection === "orders" || activeSection === "menu") return;
    const destination = activeSection === "venue-setup" ? "venue_setup" : activeSection;
    let working: ProviderWorkingLocation = { destination, context: { kind: "venue", venue_id: selectedVenue.id } };
    let hasVisibleLocation = destination !== "floor" && destination !== "schedule" && destination !== "register";
    const send = () => { if (hasVisibleLocation) postProviderWorkingLocation(user, working); };
    const receive = (event: Event) => {
      const next = (event as CustomEvent<ProviderWorkingLocation>).detail;
      if (next?.destination !== destination || next.context?.venue_id !== selectedVenue.id) return;
      working = next;
      hasVisibleLocation = true;
      send();
    };
    window.addEventListener(PROVIDER_WORKING_LOCATION_EVENT, receive);
    window.addEventListener("gaamed:workspace-ready", send);
    // Detail screens publish once their visible records have loaded.
    if (destination !== "floor" && destination !== "schedule" && destination !== "register") send();
    return () => {
      window.removeEventListener(PROVIDER_WORKING_LOCATION_EVENT, receive);
      window.removeEventListener("gaamed:workspace-ready", send);
    };
  }, [activeSection, selectedVenue?.id, user?.id, user?.role, notificationError]);

  // Repeat visits must reveal the record again without restarting authority
  // resolution when that resolution normalizes the URL with a replace.
  const notificationVisitTarget = useMemo(
    () => notificationTarget ? { ...notificationTarget } : null,
    [notificationTarget, location.key],
  );

  const navigateToSection = useCallback(
    (section: ProviderSection) => {
      navigate(getProviderSectionPath(section));
    },
    [navigate],
  );

  const openSessionCheckout = useCallback(
    (sessionId: string) => {
      formControllerRef.current.setCheckoutSessionId(sessionId);
      navigateToSection("register");
    },
    [navigateToSection],
  );

  const handleDeleteVenue = useCallback(async () => {
    if (!selectedVenue) {
      return;
    }

    if (selectedVenue.approval_status !== "approved") {
      formControllerRef.current.setVenueRemovalError(t("Only approved venues can request removal."));
      return;
    }

    const removalNote = formControllerRef.current.venueRemovalNote.trim();
    if (removalNote.length < 10) {
      formControllerRef.current.setVenueRemovalError(t("Add a removal note for admin review before requesting venue removal."));
      return;
    }

    formControllerRef.current.setVenueRemovalError(null);
    const isConfirmed = await confirmDestructiveAction({
      confirmationValue: selectedVenue.name,
      confirmButtonLabel: t("Send removal request"),
      confirmFieldLabel: t("Type {value} to send this removal request", { value: selectedVenue.name }),
      consequence: t("Admins review this request before the physical venue can be removed, so do not submit it casually."),
      description: t("This sends {name} to admins for manual removal approval using the note you entered.", { name: selectedVenue.name }),
      title: t("Request removal approval for {name}?", { name: selectedVenue.name }),
    });
    if (!isConfirmed) {
      return;
    }

    await deleteVenue(selectedVenue.id, removalNote);
    formControllerRef.current.setVenueRemovalNote("");
    formControllerRef.current.setVenueRemovalError(null);
  }, [selectedVenue, confirmDestructiveAction, deleteVenue, t]);

  const handleDeleteUnit = useCallback(async (unitId: string, unitName: string) => {
    if (!selectedVenue) {
      return;
    }

    const confirmationValue = unitName.trim() || "DELETE";
    const isConfirmed = await confirmDestructiveAction({
      confirmationValue,
      confirmButtonLabel: t("Delete unit"),
      confirmFieldLabel: t("Type {value} to permanently delete this unit", { value: confirmationValue }),
      consequence: t("The unit, its photos, and its pricing configuration are removed from this venue."),
      description: t("This permanently deletes {unitName} from {venueName}.", { unitName, venueName: selectedVenue.name }),
      title: t("Delete {name}?", { name: unitName }),
    });
    if (!isConfirmed) {
      return;
    }

    await deleteUnit(selectedVenue.id, unitId);
  }, [selectedVenue, confirmDestructiveAction, deleteUnit, t]);

  const openCreateVenue = useCallback(() => {
    formControllerRef.current.setVenuePanel("add-venue");
    navigateToSection("venue-setup");
  }, [navigateToSection]);

  const openCreateBooking = useCallback(() => {
    if (!selectedVenue || !selectedVenueCanOperate || selectedVenue.units.length === 0) {
      return;
    }

    formControllerRef.current.setCreateBookingRequestToken((current) => current + 1);
    navigateToSection("schedule");
  }, [navigateToSection, selectedVenue, selectedVenueCanOperate]);

  const openWalkIn = useCallback(() => {
    if (!selectedVenue || !selectedVenueCanOperate || selectedVenueFreeUnits === 0) {
      return;
    }

    navigateToSection("floor");
  }, [navigateToSection, selectedVenue, selectedVenueCanOperate, selectedVenueFreeUnits]);

  const openPendingReview = useCallback(() => {
    setPendingReviewToken((current) => current + 1);
    navigateToSection("schedule");
  }, [navigateToSection]);

  const dismissPendingReview = useCallback(() => {
    setDismissedPendingIds(pendingConfirmationIds);
  }, [pendingConfirmationIds]);

  const openResolvedNotificationTarget = useCallback((target: ProviderNotificationResolvedTarget, replace = false) => {
    setNotificationError(null);
    // Select only the venue returned by the current server authority check.
    if ("venueId" in target) handleSelectVenue(target.venueId);
    switch (target.kind) {
      case "booking":
        navigate(`${getProviderSectionPath("schedule")}?booking_id=${encodeURIComponent(target.bookingId)}&venue_id=${encodeURIComponent(target.venueId)}`, { replace });
        return;
      case "session": {
        const section = replace && activeSection === "register" ? "register" : "floor";
        if (section === "register") formControllerRef.current.setCheckoutSessionId(target.sessionId);
        navigate(`${getProviderSectionPath(section)}?session_id=${encodeURIComponent(target.sessionId)}&venue_id=${encodeURIComponent(target.venueId)}`, { replace });
        return;
      }
      case "food-order":
        navigate(`${getProviderSectionPath("floor")}?session_id=${encodeURIComponent(target.sessionId)}&order_id=${encodeURIComponent(target.orderId)}&venue_id=${encodeURIComponent(target.venueId)}`, { replace });
        return;
      case "venue":
        if (!replace) navigate(getProviderSectionPath("venue-setup"));
        return;
      case "pending-confirmations":
        setPendingReviewToken((current) => current + 1);
        navigate(`${getProviderSectionPath("schedule")}?notification=pending-confirmations`, { replace });
        return;
      case "inbox":
        navigate(getProviderSectionPath("dashboard"), { replace });
    }
  }, [activeSection, handleSelectVenue, navigate]);
  const openResolvedNotificationTargetRef = useRef(openResolvedNotificationTarget);
  openResolvedNotificationTargetRef.current = openResolvedNotificationTarget;

  const explainUnavailableNotification = useCallback((error?: unknown) => {
    const requiresSignIn = error instanceof ApiError && error.status === 401;
    setNotificationError({ requiresSignIn, message: requiresSignIn
      ? "Your sign-in has expired. Sign in again to open this notification."
      : error !== undefined && !(error instanceof ApiError && (error.status === 403 || error.status === 404))
        ? "Could not load this notification's record. Check your connection and open the notification again."
        : "This notification's record is no longer available or you do not have access to it." });
  }, []);

  const handleOpenNotification = useCallback(
    async (item: ProviderNotificationItem) => {
      setNotificationError(null);
      const identityKey = notificationIdentityKey;
      const generation = ++notificationResolutionGenerationRef.current;
      if (!isCurrentNotificationResolution(identityKey, generation)) return;
      const target: ProviderNotificationAuthorityTarget | null = "dto" in item
        ? item.target.availability === "available" ? item.target.target : null
        : parseProviderNotificationTarget(item.targetSearch);
      if (!target) {
        if (!isCurrentNotificationResolution(identityKey, generation)) return;
        if ("dto" in item) markNotificationTargetUnavailable(item.id, item.target.availability === "unavailable" ? item.target.reason : "invalid");
        explainUnavailableNotification();
        return;
      }
      await resolveAndRouteProviderNotificationTarget(
        target,
        providerApi,
        () => isCurrentNotificationResolution(identityKey, generation),
        {
          onResolved: (resolved) => {
            if (isCurrentNotificationResolution(identityKey, generation)) {
              openResolvedNotificationTarget(resolved);
              void markNotificationRead(item.id);
            }
          },
          onUnavailable: (error) => {
            if (!isCurrentNotificationResolution(identityKey, generation)) return;
            // Temporary failures must leave the inbox action available for retry.
            if (error === undefined || error instanceof ApiError && (error.status === 403 || error.status === 404)) {
              markNotificationTargetUnavailable(item.id, error instanceof ApiError && error.status === 403 ? "unauthorized" : "stale");
            }
            if (isCurrentNotificationResolution(identityKey, generation)) explainUnavailableNotification(error);
          },
        },
      );
    },
    [explainUnavailableNotification, isCurrentNotificationResolution, markNotificationRead, markNotificationTargetUnavailable, notificationIdentityKey, openResolvedNotificationTarget],
  );

  const handleMarkAllNotificationsRead = useCallback(async () => {
    const saved = await markAllNotificationsRead();
    if (saved && pendingConfirmationCount > 0) {
      setDismissedPendingIds(pendingConfirmationIds);
    }
  }, [markAllNotificationsRead, pendingConfirmationCount, pendingConfirmationIds]);

  useEffect(() => {
    setNotificationError(null);
    if (!notificationTarget) {
      notificationResolutionGenerationRef.current += 1;
      lastNotificationTargetKeyRef.current = null;
      return () => {
        notificationResolutionGenerationRef.current += 1;
      };
    }
    const identityKey = notificationIdentityKey;
    if (!identityKey) {
      notificationResolutionGenerationRef.current += 1;
      lastNotificationTargetKeyRef.current = null;
      return () => {
        notificationResolutionGenerationRef.current += 1;
      };
    }
    const targetKey = `${identityKey}:${notificationTarget.kind}:${location.search}`;
    if (lastNotificationTargetKeyRef.current === targetKey) {
      return () => {
        notificationResolutionGenerationRef.current += 1;
        if (lastNotificationTargetKeyRef.current === targetKey) {
          lastNotificationTargetKeyRef.current = null;
        }
      };
    }
    lastNotificationTargetKeyRef.current = targetKey;
    const generation = ++notificationResolutionGenerationRef.current;
    void resolveAndRouteProviderNotificationTarget(
      notificationTarget,
      providerApi,
      () => isCurrentNotificationResolution(identityKey, generation),
      {
        onResolved: (resolved) => {
          if (isCurrentNotificationResolution(identityKey, generation)) openResolvedNotificationTargetRef.current(resolved, true);
        },
        onUnavailable: (error) => {
          if (isCurrentNotificationResolution(identityKey, generation)) explainUnavailableNotification(error);
        },
      },
    );
    return () => {
      notificationResolutionGenerationRef.current += 1;
      if (lastNotificationTargetKeyRef.current === targetKey) {
        lastNotificationTargetKeyRef.current = null;
      }
    };
  }, [
    location.search,
    notificationTarget,
    notificationIdentityKey,
    isCurrentNotificationResolution,
    explainUnavailableNotification,
  ]);

  // Counts shown as badges next to nav labels. Labels stay short so they never truncate.
  const sectionBadges = useMemo(() => {
    const badges: Partial<Record<ProviderSection, NavBadge>> = {};
    if (sessions.length > 0) badges.floor = { count: sessions.length, tone: "info", label: t("{count} playing now", { count: sessions.length }) };
    if (openOrderCount > 0) badges.orders = { count: openOrderCount, tone: newOrderCount > 0 ? "attention" : "info", label: t("{count} open orders", { count: openOrderCount }) };
    if (pendingConfirmationCount > 0) badges.schedule = { count: pendingConfirmationCount, tone: "attention", label: t("{count} pending", { count: pendingConfirmationCount }) };
    return badges;
  }, [newOrderCount, openOrderCount, pendingConfirmationCount, sessions.length, t]);

  useEffect(() => {
    mainScrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [activeSection]);

  useEffect(() => {
    if (summary.provider_status === "approved") {
      startRealtimeEvents();
    }
    return () => {
      stopRealtimeEvents();
    };
  }, [summary.provider_status]);

  const stableValue = useMemo(
    () => ({
      summary,
      provider,
      venues,
      selectedVenue,
      selectedUnit,
      selectedVenueId,
      selectedUnitId,
      sessions,
      recentSessions,
      activeSessionsByBookingId,
      isOnline,
      pendingConfirmationCount,
      notificationTarget: notificationVisitTarget,
      setSelectedVenueId,
      setSelectedUnitId,
      navigateToSection,
      openCreateVenue,
      openCreateBooking,
      openWalkIn,
      openSessionCheckout,
      syncOperationalState,
      handleReceiptChange,
      handleSelectVenue,
      handleDeleteVenue,
      handleDeleteUnit,
      startSessionFromBooking,
      startWalkInSession,
      extendSession,
      stopSession,
      reloadSessions,
      createVenue,
      updateVenue,
      deleteVenue,
      createUnit,
      updateUnit,
      deleteUnit,
      updateAvailability,
      createBlackoutWindow,
      deleteBlackoutWindow,
      reload,
      isLoading,
      sessionsLoading,
      loadError,
      saveError,
      notice,
      sessionError,
      sessionNotice,
      creatingVenue,
      updatingVenue,
      deletingVenue,
      creatingUnit,
      updatingUnit,
      deletingUnit,
      updatingAvailability,
      creatingBlackoutWindow,
      deletingBlackoutWindowId,
      creatingWalkIn,
      startingBookingId,
      extendingSessionId,
      stoppingSessionId,
      t,
      user,
      signOut: handleProviderSignOut,
      language,
      setLanguage,
      themePreference,
      setThemePreference,
    }),
    [
      pendingConfirmationCount,
      summary,
      provider,
      venues,
      selectedVenue,
      selectedUnit,
      selectedVenueId,
      selectedUnitId,
      sessions,
      recentSessions,
      activeSessionsByBookingId,
      isOnline,
      notificationVisitTarget,
      setSelectedVenueId,
      setSelectedUnitId,
      navigateToSection,
      openCreateVenue,
      openCreateBooking,
      openWalkIn,
      openSessionCheckout,
      syncOperationalState,
      handleReceiptChange,
      handleSelectVenue,
      handleDeleteVenue,
      handleDeleteUnit,
      startSessionFromBooking,
      startWalkInSession,
      extendSession,
      stopSession,
      reloadSessions,
      createVenue,
      updateVenue,
      deleteVenue,
      createUnit,
      updateUnit,
      deleteUnit,
      updateAvailability,
      createBlackoutWindow,
      deleteBlackoutWindow,
      reload,
      isLoading,
      sessionsLoading,
      loadError,
      saveError,
      notice,
      sessionError,
      sessionNotice,
      creatingVenue,
      updatingVenue,
      deletingVenue,
      creatingUnit,
      updatingUnit,
      deletingUnit,
      updatingAvailability,
      creatingBlackoutWindow,
      deletingBlackoutWindowId,
      creatingWalkIn,
      startingBookingId,
      extendingSessionId,
      stoppingSessionId,
      t,
      user,
      handleProviderSignOut,
      language,
      setLanguage,
      themePreference,
      setThemePreference,
    ],
  );

  const formValue = useMemo(
    () => ({
      operationsVersion,
      setOperationsVersion,
      createBookingRequestToken,
      setCreateBookingRequestToken,
      pendingReviewToken,
      setPendingReviewToken,
      checkoutSessionId,
      setCheckoutSessionId,
      venuePanel,
      setVenuePanel,
      venueRemovalNote,
      setVenueRemovalNote,
      venueRemovalError,
      setVenueRemovalError,
    }),
    [
      operationsVersion,
      setOperationsVersion,
      createBookingRequestToken,
      setCreateBookingRequestToken,
      pendingReviewToken,
      setPendingReviewToken,
      checkoutSessionId,
      setCheckoutSessionId,
      venuePanel,
      setVenuePanel,
      venueRemovalNote,
      setVenueRemovalNote,
      venueRemovalError,
      setVenueRemovalError,
    ],
  );

  formControllerRef.current = formValue;

  if (summary.provider_status && summary.provider_status !== "approved") {
    return (
      <ProviderApprovalBlockedPage
        editRegistrationHref={
          summary.provider_status === "rejected" && user?.role === "provider_owner"
            ? "/provider-register?mode=resubmit"
            : undefined
        }
        isRefreshing={isLoading}
        onRefresh={async () => {
          await reload();
        }}
        onSignOut={handleProviderSignOut}
        reviewNote={summary.provider_review_note}
        status={summary.provider_status}
        summary={summary}
        userLabel={user?.display_name ?? user?.email ?? "Unknown user"}
      />
    );
  }
  const nextLanguage = language === "ar" ? "en" : "ar";
  const nextThemePreference = themePreference === "dark" ? "light" : "dark";
  const languageToggleLabel = language === "ar" ? t("preferences.language.toggleToEn") : t("preferences.language.toggleToAr");
  const themeToggleLabel = themePreference === "dark" ? t("preferences.theme.toggleToLight") : t("preferences.theme.toggleToDark");
  const ThemeToggleIcon = themePreference === "dark" ? SunMedium : MoonStar;

  const preferenceControls = (
    <div className="provider-sidebar-preferences flex flex-wrap items-center gap-2" aria-label={t("preferences.title")}>
      <button
        aria-label={languageToggleLabel}
        className="ops-button ops-preference-toggle"
        data-size="sm"
        data-variant="secondary"
        onClick={() => setLanguage(nextLanguage)}
        title={languageToggleLabel}
        type="button"
      >
        <span aria-hidden="true" className="ops-preference-toggle-badge"><Languages size={15} strokeWidth={2.2} /></span>
        <span className="ops-preference-toggle-copy">{t(nextLanguage === "en" ? "preferences.language.en" : "preferences.language.ar")}</span>
      </button>
      <button
        aria-label={themeToggleLabel}
        className="ops-button ops-preference-toggle"
        data-size="sm"
        data-variant="secondary"
        onClick={() => setThemePreference(nextThemePreference)}
        title={themeToggleLabel}
        type="button"
      >
        <span aria-hidden="true" className="ops-preference-toggle-badge"><ThemeToggleIcon size={15} strokeWidth={2.2} /></span>
        <span className="ops-preference-toggle-copy">{t(nextThemePreference === "light" ? "preferences.theme.light" : "preferences.theme.dark")}</span>
      </button>
      <button
        className="ops-button ops-preference-toggle provider-sound-toggle"
        data-size="sm"
        data-variant="secondary"
        onClick={() => setSoundEnabled(!soundEnabled)}
        title={t("Plays a chime for new orders and bookings. Tap to switch.")}
        type="button"
      >
        <span aria-hidden="true" className="ops-preference-toggle-badge">{soundEnabled ? <Volume2 size={15} strokeWidth={2.2} /> : <VolumeX size={15} strokeWidth={2.2} />}</span>
        <span className="ops-preference-toggle-copy">{soundEnabled ? t("Sound on") : t("Sound off")}</span>
      </button>
    </div>
  );

  const renderSidebarLinks = (sections: typeof primarySections) => (
    sections.map((section) => {
      const Icon = sectionIcons[section.id];
      return (
        <NavLink
          key={section.id}
          to={getProviderSectionPath(section.id)}
          aria-label={t(sectionLabelKeys[section.id])}
          className="provider-sidebar-button"
          data-active={activeSection === section.id ? "true" : undefined}
          data-secondary={secondarySections.some((s) => s.id === section.id) ? "true" : undefined}
          title={t(sectionLabelKeys[section.id])}
        >
          <span className="provider-sidebar-label">
            {Icon ? <Icon className="provider-sidebar-icon" /> : null}
            <span className="provider-sidebar-label-text">{t(sectionLabelKeys[section.id])}</span>
          </span>
          <NavBadgeChip badge={sectionBadges[section.id]} />
        </NavLink>
      );
    })
  );

  const notificationsPanel = getAssistance() ? null : (
    <ProviderNotificationsPanel
      notifications={notifications}
      readBusy={notificationReadBusy}
      readError={notificationReadError}
      inboxState={notificationInboxState}
      onLoadMore={loadMoreNotifications}
      onDismissPending={dismissPendingReview}
      onMarkAllRead={handleMarkAllNotificationsRead}
      onMarkNotificationRead={markNotificationRead}
      onOpenNotification={handleOpenNotification}
      onRefresh={reloadNotifications}
      onReviewPending={openPendingReview}
      pendingCount={pendingConfirmationCount}
      pendingUnread={pendingUnread}
      pagination={notificationPagination}
      preferences={{
        onChange: updateNotificationPreference,
        onRetry: () => {
          void reloadNotificationPreferences();
        },
        state: notificationPreferences,
      }}
      unreadCount={unreadNotificationsCount}
    />
  );

  return (
    <div className="provider-shell">
      {destructiveActionDialog}
      {elevationDialog}

      <aside className="provider-sidebar">
        <div className="provider-sidebar-brand" title={provider?.name ?? t(summary.title)}>
          <span className="provider-brand-mark">
            {(provider?.name ?? t(summary.title)).slice(0, 2).toUpperCase()}
          </span>
          <div className="provider-sidebar-brand-copy">
            <span className="provider-sidebar-eyebrow">{t("Provider workspace")}</span>
            <span className="provider-brand-name">{provider?.name ?? t(summary.title)}</span>
          </div>
        </div>

        <section className="provider-sidebar-venue-block" aria-label={t("Active venue")}>
          <div className="provider-sidebar-venue-heading">
            <span className="provider-sidebar-eyebrow">{t("Active venue")}</span>
            {selectedVenue ? (
              <span
                className="ops-chip shrink-0"
                data-tone={selectedVenueOpenState?.isOpenNow ? "success" : "warning"}
                title={`${selectedVenueOpenState?.reason ?? ""} · ${t("for bookings, from your opening hours")}`}
              >
                {selectedVenueOpenState?.isOpenNow ? t("Open now") : t("Closed")}
              </span>
            ) : null}
          </div>
          <div className="provider-sidebar-venue-select">
            <VenueSwitcher
              onSelect={handleSelectVenue}
              selectedVenueId={selectedVenueId}
              venues={venues}
            />
          </div>
        </section>

        <nav className="provider-sidebar-nav" aria-label={t("provider.sectionsMenu")}>
          {renderSidebarLinks(primarySections)}
        </nav>

        <div className="provider-sidebar-divider" />
        <nav className="provider-sidebar-nav provider-sidebar-nav-secondary" aria-label={t("provider.setupMenu")}>
          {renderSidebarLinks(secondarySections)}
        </nav>

        {notificationsPanel ? (
          <div className="provider-sidebar-notifications">
            <button
              aria-expanded={sidebarNotificationsOpen}
              aria-haspopup="true"
              className="provider-sidebar-button"
              data-active={sidebarNotificationsOpen ? "true" : undefined}
              onClick={() => setSidebarNotificationsOpen((open) => !open)}
              type="button"
            >
              <span className="provider-sidebar-label">
                <Bell className="provider-sidebar-icon" />
                <span className="provider-sidebar-label-text">{t("Notifications")}</span>
              </span>
              <NavBadgeChip
                badge={unreadNotificationsCount > 0
                  ? { count: unreadNotificationsCount, tone: "attention", label: t("{count} unread", { count: unreadNotificationsCount }) }
                  : undefined}
              />
            </button>
          </div>
        ) : null}

        <div className="provider-sidebar-account">
          <div className="provider-sidebar-account-copy">
            <span className="provider-sidebar-user" title={user?.email ?? undefined}>{user?.display_name || user?.email}</span>
            <span className="provider-sidebar-membership" title={t("Workspace role")}>
              {summary.membership_role ? membershipRoleLabel(summary.membership_role, t) : roleLabel(user?.role, t)}
            </span>
          </div>
          <div className="provider-sidebar-account-actions">
            {preferenceControls}
            <button
              className="ops-button provider-sidebar-signout"
              data-size="sm"
              data-variant="secondary"
              onClick={() => void handleProviderSignOut()}
              type="button"
            >
              {t("common.signOut")}
            </button>
          </div>
        </div>
      </aside>

      {sidebarNotificationsOpen && notificationsPanel ? (
        <div className="pn-drawer-layer">
          <button
            aria-label={t("Close notifications")}
            className="pn-drawer-backdrop"
            onClick={() => setSidebarNotificationsOpen(false)}
            tabIndex={-1}
            type="button"
          />
          <div
            className="pn-drawer"
            onClick={(event) => {
              // Opening a notification takes you to it, so the drawer gets out of the way.
              const target = event.target as HTMLElement | null;
              if (target?.closest('[data-testid="provider-notification-item"], [data-testid="provider-notification-pending"] .ops-button')) {
                setSidebarNotificationsOpen(false);
              }
            }}
          >
            <button
              aria-label={t("Close notifications")}
              autoFocus
              className="pn-drawer-close"
              onClick={() => setSidebarNotificationsOpen(false)}
              type="button"
            >
              <X aria-hidden="true" size={18} />
            </button>
            {notificationsPanel}
          </div>
        </div>
      ) : null}

      <div className="provider-body">
        <main className="provider-main" ref={mainScrollRef}>
          <MobileHeader
            activeSection={activeSection}
            language={language}
            nextLanguage={nextLanguage}
            nextThemePreference={nextThemePreference}
            notificationsPanel={notificationsPanel}
            onSelectVenue={handleSelectVenue}
            onSetLanguage={setLanguage}
            onSetThemePreference={setThemePreference}
            onSignOut={handleProviderSignOut}
            pendingCount={pendingConfirmationCount}
            sectionBadges={sectionBadges}
            providerName={provider?.name ?? t(summary.title)}
            selectedVenueId={selectedVenueId}
            t={t}
            themePreference={themePreference}
            unreadNotificationCount={unreadNotificationsCount}
            userRole={user?.role}
            venues={venues}
          />

          {notice ? (
            <div className="ops-banner" data-tone="success">
              {notice}
            </div>
          ) : null}

          {!isOnline || pendingCount > 0 || lastSyncError ? (
            <div className="ops-surface ops-surface-dense">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">
                    {!isOnline
                      ? t("Offline mode is active.")
                      : isSyncing
                        ? t("Syncing queued provider changes.")
                        : pendingCount > 0
                          ? t("{count} queued", { count: pendingCount })
                          : t("Provider sync needs attention.")}
                  </p>
                  {lastSyncError ? (
                    <p className="mt-1 text-xs leading-5 text-amber-100">
                      {t("Sync issue: {error}", { error: lastSyncError })}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="ops-chip" data-tone={isOnline ? "success" : "warning"}>
                    {isOnline ? t("Online") : t("Offline")}
                  </span>
                  <span className="ops-chip">{t("{count} queued", { count: pendingCount })}</span>
                  <button
                    className="ops-button"
                    data-variant="secondary"
                    disabled={!isOnline || isSyncing || pendingCount === 0}
                    onClick={() => void syncPendingMutations()}
                    type="button"
                  >
                    {isSyncing ? t("Syncing...") : t("Sync now")}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <DeadLetterPanel deadLetters={deadLetters} onDismiss={dismissDeadLetter} onRetry={retryDeadLetter} />

          {loadError || saveError ? (
            <div className="ops-banner" data-tone="danger">
              <span className="min-w-0 flex-1">{loadError ?? saveError}</span>
              {loadError ? (
                <Button
                  onClick={() => void reload()}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  {t("Retry")}
                </Button>
              ) : null}
            </div>
          ) : null}

          {notificationError ? (
            <div className="ops-banner" data-tone="danger" role="alert">
              <span>{t(notificationError.message)}</span>
              {notificationError.requiresSignIn ? (
                <button className="ops-button" type="button" onClick={() => {
                  const returnTo = location.pathname + location.search;
                  void signOut().finally(() => navigate(`/sign-in?returnTo=${encodeURIComponent(returnTo)}`)).catch(() => undefined);
                }}>{t("Sign in")}</button>
              ) : null}
              <button className="ops-button" type="button" onClick={() => {
                setNotificationError(null);
                navigate(getProviderSectionPath("floor"));
              }}>{t("Back to floor")}</button>
            </div>
          ) : null}

          <ProviderWorkspaceProvider value={stableValue}>
            <ProviderWorkspaceFormProvider value={formValue}>
              {notificationError ? null : <Outlet />}
            </ProviderWorkspaceFormProvider>
          </ProviderWorkspaceProvider>
          <AppUpdateBanner />
        </main>
      </div>
    </div>
  );
}
